import { toast as libToast } from 'sonner';

/**
 * The single entry point for app toasts. Everything user-facing goes through
 * here so that at most `MAX_VISIBLE_TOASTS` are on screen and the rest wait
 * their turn — importing `sonner` directly bypasses the queue and lets toasts
 * stack (see the `no-restricted-imports` rule in eslint.config.js).
 *
 * Copy rules for the messages passed in (D15 in .claude/skills/check-tone/CHECKLIST.md):
 * - success reads "Your [noun] has been [verb]." and never says "successfully"
 *   ("Your photo has been deleted.").
 * - errors read "Couldn’t [verb] [noun]." plus a way out when the user can act on it
 *   ("Couldn’t delete the photo. Please try again.").
 * - every message ends with a period and keeps to eight words or fewer (D13).
 */

const MAX_VISIBLE_TOASTS = 3;

type ToastType = 'success' | 'info' | 'warning' | 'error';

type ToastOptions = {
  /**
   * Collapses repeats: while a toast with this id is showing or queued, further
   * toasts with the same id are dropped. Used for failures that arrive in bulk,
   * such as several requests failing on one dead connection.
   */
  id?: string;
  duration?: number;
  /** A button on the toast — "Retry" on a failed upload, say. */
  action?: { label: string; onClick: () => void };
};

type QueuedToast = { type: ToastType; message: string } & ToastOptions;

const pending: QueuedToast[] = [];
const activeIds = new Set<string>();
let visibleCount = 0;

function show(queued: QueuedToast) {
  visibleCount += 1;
  if (queued.id) {
    activeIds.add(queued.id);
  }
  let released = false;

  const release = () => {
    if (released) {
      return;
    }
    released = true;
    visibleCount -= 1;
    if (queued.id) {
      activeIds.delete(queued.id);
    }
    flush();
  };

  libToast[queued.type](queued.message, {
    id: queued.id,
    duration: queued.duration,
    // Sonner's action click only deletes the toast — neither onDismiss nor onAutoClose runs —
    // so the slot is released here, or every Retry pressed would hold one for good.
    action: queued.action && {
      label: queued.action.label,
      onClick: () => {
        release();
        queued.action?.onClick();
      },
    },
    onAutoClose: release,
    onDismiss: release,
  });
}

function flush() {
  while (visibleCount < MAX_VISIBLE_TOASTS && pending.length > 0) {
    show(pending.shift()!);
  }
}

const enqueue = (type: ToastType, message: string, options?: ToastOptions) => {
  const id = options?.id;
  if (id && (activeIds.has(id) || pending.some((queued) => queued.id === id))) {
    return;
  }
  pending.push({ type, message, ...options });
  flush();
};

const success = (message: string, options?: ToastOptions) => {
  enqueue('success', message, options);
};

const info = (message: string, options?: ToastOptions) => {
  enqueue('info', message, options);
};

const warning = (message: string, options?: ToastOptions) => {
  enqueue('warning', message, options);
};

const error = (message: string, options?: ToastOptions) => {
  enqueue('error', message, options);
};

export const toast = {
  success,
  info,
  warning,
  error,
};
